﻿import { isHTMLElement, isNumber, isString } from "../../Shared/typeGuards";
import { getFieldType } from "./HubSpotFormFieldType";

/**
 * A representation of a HubSpot Form.
 * Supports step transitions.
 */
export class HubSpotForm {
  constructor(formWrapper: HTMLElement) {
    this.formWrapper = formWrapper;
    this._activeStep = 0;
  }

  private _activeStep: number;

  readonly formWrapper: HTMLElement;

  get form(): HTMLFormElement | null {
    return this.formWrapper.querySelector<HTMLFormElement>("form");
  }

  get formDividers() {
    return (
      this.formWrapper.dataset["formDividers"]
        ?.split(",")
        // Filter out empty results
        .filter(formDivider => !!formDivider) ?? []
    );
  }

  /**
   * Gets the active step. Steps are indexed from 0.
   */
  get activeStep() {
    return this._activeStep;
  }

  get numberOfSteps() {
    return this.formDividers.length + 1;
  }

  get isActiveStepComplete() {
    return this.isStepComplete(this.activeStep);
  }

  readonly isStepComplete = (step: number) =>
    this.areRequiredFieldsInStep(step)
      ? this.areRequiredFieldsInStepComplete(step)
      : this.areAllFieldsInStepComplete(step);

  readonly stepToFormDivider = (step: number) => this.formDividers[step - 1];
  readonly formDividerToStep = (formDivider?: string) =>
    formDivider ? this.formDividers.indexOf(formDivider) + 1 : 0;

  readonly moveToStep = (step: number) => {
    for (let currentStep = 0; currentStep < this.numberOfSteps; currentStep++) {
      const fieldsets = this.getFieldsetsInStep(currentStep);

      fieldsets.forEach(fieldset =>
        currentStep <= step
          ? fieldset.classList.remove("hs-hidden")
          : fieldset.classList.add("hs-hidden")
      );
    }

    // If it's the last step - show the submit button and recaptcha
    if (step + 1 >= this.numberOfSteps) {
      this.submitWrapper?.classList.remove("hs-hidden");
      this.recaptchaWrapper?.classList.remove("hs-hidden");
    } else {
      this.submitWrapper?.classList.add("hs-hidden");
      this.recaptchaWrapper?.classList.add("hs-hidden");
    }

    this._activeStep = step;
  };

  get areRequiredFieldsInActiveStep() {
    return this.areRequiredFieldsInStep(this.activeStep);
  }

  readonly areRequiredFieldsInStep = (step: number) =>
    this.getRequiredFieldsInStep(step).length !== 0;

  get areRequiredFieldsInActiveStepComplete() {
    return this.areRequiredFieldsInStepComplete(this.activeStep);
  }

  /**
   * Checks whether all required fields in the given step have a value and are without errors.
   */
  readonly areRequiredFieldsInStepComplete = (step: number) =>
    this.getRequiredFieldsInStep(step).every(this.isFieldComplete);

  get areAllFieldsInActiveStepComplete() {
    return this.areAllFieldsInStepComplete(this.activeStep);
  }

  get formFieldsets() {
    return [...(this.form?.querySelectorAll<HTMLFieldSetElement>("&>fieldset") ?? [])];
  }

  get formFields() {
    return this.formFieldsets.flatMap(this.getFieldsetFields);
  }

  /**
   * Checks whether all fields in the given step have a value and are without errors.
   */
  readonly areAllFieldsInStepComplete = (step: number) =>
    this.getFieldsInStep(step).every(this.isFieldComplete);

  /**
   * Scans all form fields and sets the active step accordingly.
   */
  readonly refreshActiveStep = () => {
    let step = 0;

    do {
      if (this.isStepComplete(step)) {
        step++;
      } else {
        break;
      }
    } while (step < this.numberOfSteps - 1);

    return step;
  };

  private get submitWrapper() {
    return this.form?.querySelector(".hs-submit.hs_submit");
  }

  private get recaptchaWrapper() {
    return this.form?.querySelector(".hs_recaptcha");
  }

  /**
   * Sets a field value.
   * @param field A field name, order (indexed from 0) or a field element.
   * Warning: Accessing by index can be inconsistent as dependent fields can change order.
   * @param value A new value.
   * @returns True if a value was correctly set; false otherwise.
   */
  readonly setFieldValue = (
    field: number | string | HTMLElement,
    value: string
  ): boolean => {
    let fieldElement: HTMLElement | undefined = undefined;

    if (isString(field)) {
      fieldElement = this.getFieldByName(field);
    } else if (isNumber(field)) {
      fieldElement = this.getFieldByIndex(field);
    } else if (isHTMLElement(field)) {
      fieldElement = field;
    }

    if (fieldElement) {
      return this.internalSetFieldValue(fieldElement, value);
    }

    return false;
  };

  private readonly getRequiredFieldsInStep = (step: number) =>
    this.getFieldsInStep(step).filter(this.isFieldRequired);

  private readonly isFieldRequired = (field: Element) =>
    !!field.querySelector("[required]");

  private readonly getFieldsInStep = (step: number) =>
    this.getFieldsetsInStep(step).flatMap(this.getFieldsetFields);

  private readonly getFieldsetsInStep = (step: number) => {
    const formDividers = this.formDividers;
    let currentStep = 0;

    const results: HTMLFieldSetElement[] = [];
    let carryOver: HTMLFieldSetElement[] = [];

    for (const fieldset of this.formFieldsets) {
      const fieldNames = this.getFieldsetFieldNames(fieldset);

      if (fieldNames.some(name => formDividers.includes(name))) {
        currentStep++;
      }

      // Purely decorative fieldsets should be carried over to the next normal fieldset.
      // This solves the issue when purely decorative fieldsets are just before a form divider.
      if (!this.doesFieldsetContainInput(fieldset)) {
        carryOver.push(fieldset);
      } else if (currentStep !== step) {
        // Fieldset with inputs, but not in the requested step should reset what's carried over.
        carryOver = [];
      } else {
        results.push(...carryOver);
        results.push(fieldset);

        carryOver = [];
      }

      if (currentStep > step) {
        break;
      }
    }

    return results;
  };

  private readonly getFieldByName = (fieldName: string) =>
    this.formFields.find(field => this.getFieldName(field) === fieldName);
  private readonly getFieldByIndex = (index: number) => this.formFields[index];

  private readonly getFieldName = (field: Element) =>
    field.classList?.item(1)?.replace(/^(.*\/)|(hs-)/, "") ?? "";

  private readonly getFieldsetFields = (fieldset: HTMLFieldSetElement) => [
    ...fieldset.querySelectorAll<HTMLElement>(".field.hs-form-field"),
  ];

  private readonly getFieldsetFieldNames = (fieldset: HTMLFieldSetElement) =>
    this.getFieldsetFields(fieldset).map(this.getFieldName);

  private readonly isFieldComplete = (field: Element) =>
    this.hasFieldAnyValue(field) && !this.hasFieldAnyError(field);

  /**
   * Checks if a fieldset is purely decorative, e.g. contains only richtext elements.
   */
  private readonly doesFieldsetContainInput = (fieldset: HTMLFieldSetElement) =>
    !!fieldset.querySelector("input");

  private readonly hasFieldAnyValue = (field: Element) => {
    const fieldType = getFieldType(field);

    // Special handling for checkboxes and radio
    // If the checkbox/radio is not required, then it's assumed to be completed.
    // If it is required, then at least 1 input must be checked.
    if (fieldType === "checkbox" || fieldType === "radio") {
      return !this.isFieldRequired(field) || field.querySelector("input:checked");
    }

    // Special handling for attachments
    // If the file attachment is not required, then it's assumed to be completed.
    if (fieldType === "file") {
      return !this.isFieldRequired(field) || field.querySelector("input")?.value;
    }

    const input = field.querySelector("input,textarea,select");
    if (input && "value" in input) {
      return !!input.value;
    }

    return false;
  };

  private readonly internalSetFieldValue = (
    field: HTMLElement,
    value: string
  ): boolean => {
    const fieldType = getFieldType(field);
    let success = false;
    const inputsChanged: HTMLElement[] = [];

    switch (fieldType) {
      case "radio": {
        // Expected: a string that is the option that should be selected
        const radioButton = field.querySelector<HTMLInputElement>(
          `input[value="${value}"]`
        );
        if (!radioButton) {
          console.error(`Could not find a radio button with value ${value}`);
          break;
        }

        radioButton.checked = true;
        inputsChanged.push(radioButton);
        success = true;
        break;
      }

      case "checkbox": {
        // Expected: a string with options that should be selected, divided by a comma
        const values = value.split(",").filter(s => s);
        let checkboxSuccess = true;

        // Clear default selection
        field
          .querySelectorAll<HTMLInputElement>("input")
          .forEach(input => (input.checked = false));

        for (const valueToBeSelected of values) {
          const input = field.querySelector<HTMLInputElement>(
            `input[value="${valueToBeSelected}"]`
          );
          if (input) {
            input.checked = true;
            inputsChanged.push(input);
          } else {
            checkboxSuccess = false;
            console.error(`Could not find a checkbox with value ${valueToBeSelected}`);
          }
        }

        success = checkboxSuccess;
        break;
      }

      case "select":
      default: {
        const input = field.querySelector("input,select") as
          | HTMLInputElement
          | HTMLSelectElement;
        if (input) {
          input.value = value;
          success = true;
          inputsChanged.push(input);
        }
        break;
      }
    }

    // Notify about the change to trigger the validation and dependent fields
    inputsChanged.forEach(input =>
      input.dispatchEvent(new Event("change", { bubbles: true }))
    );
    return success;
  };

  private readonly hasFieldAnyError = (field: Element) =>
    !!field.querySelector(".hs-error-msg");
}
